import React from 'react';
import PaymentSelector from './paymentInputs/PaymentSelector';
import {connect} from 'react-redux';
import {orders, paymentProfiles} from '../../ducks/index';
import registeredComponent from "../../utils/registeredComponent";
import translatePaymentDetails from './translatePaymentDetails';

const defaultAfterSubmit = (dispatch) => ({order}) => dispatch(orders.actions.onCompleteRedirect(order));

const mapStateToProps = (state, ownProps) => ({
  showSaveProfileOption: true
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  afterSubmit: ownProps.afterSubmit || defaultAfterSubmit(dispatch),

  /**
   * Handles order submission.
   *
   * @param payment_method
   * @param token
   * @param account_id
   * @param savePaymentProfile
   * @param autopay
   * @param paymentDetails
   * @param {function} capturePayment (optional) if present indicates that the Stripe payment intent order flow
   *   should be used. This function is called to capture the payment client-side.
   * @param {object} options gateway specific options passed to `orders.actions.create` with the `options`
   *   parameter.
   * @returns {Promise<{payment_profile: *, order: *}>}
   */
  onSubmit: async ({
    payment_method,
    token,
    account_id,
    savePaymentProfile,
    autopay,
    paymentDetails,
    capturePayment,
    captureProfile,
    options: orderOptions = {}
  }) => {
    const translatedDetails = translatePaymentDetails(payment_method, paymentDetails);

    const createOrder = (orderProps = {}, options = {}) => dispatch(
      orders.actions.create(
        { ...ownProps.order, ...translatedDetails, ...orderProps },
        { token, account_id, ...options }
      )
    )
      .then(orders.actions.denormalizeObjects);

    const completeOrder = (order, options) => dispatch(orders.actions.complete(order, options))
      .then(orders.actions.denormalizeObjects);

    const cancelOrder = (order) => dispatch(orders.actions.cancel(order))
      .then(orders.actions.denormalizeObjects);

    const saveProfile = (token, options = {}) => {
      if (savePaymentProfile && token) {
        return dispatch(paymentProfiles.actions.create({
          ...translatedDetails,
          autopay
        }, {
          token,
          account_id,
          ...options
        }))
          .then(paymentProfiles.actions.denormalizeObjects);
      }
      return null;
    };

    let paymentProfile;
    let order;

    if (savePaymentProfile) {
      if (captureProfile) {
        const result = await captureProfile();
        paymentProfile = await saveProfile(result.token);
      } else {
        paymentProfile = await saveProfile(token);
      }

      const { payment_verification: paymentVerification } = paymentProfile;
      if (paymentVerification && paymentVerification.status !== 'verified') {
        order = null;
      } else {
        order = await createOrder(
          { payment_method: 'payment profile' },
          { token: paymentProfile.id, account_id: null }
        );
      }
    } else {
      paymentProfile = null;

      if (capturePayment) {
        const initialOrder = await createOrder({}, {
          start: true,
          options: { ...orderOptions }
        });

        if (initialOrder.state === 'payment_declined') {
          throw { message: 'There was an error starting the transaction. Please try again.' };
        }

        const result = await capturePayment(initialOrder).catch((error) => {
          cancelOrder(initialOrder);
          throw error;
        });
        order = await completeOrder(initialOrder, result.options);
      } else {
        order = await createOrder();
      }
    }

    return {
      order,
      payment_profile: paymentProfile
    };
  }
});

export default registeredComponent("OrderForm")(connect(mapStateToProps, mapDispatchToProps)(PaymentSelector));
